import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage, fmtDate } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useSeo } from "@/lib/seo";
import { Copy, Send, Loader2, RefreshCw } from "lucide-react";

const Code = ({ children, testid }) => {
  const copy = () => { navigator.clipboard.writeText(children); toast.success("Copied"); };
  return (
    <div className="relative group min-w-0 max-w-full overflow-hidden rounded-lg">
      <pre className="overflow-auto scrollbar-thin rounded-lg bg-secondary p-4 text-xs font-mono leading-5 whitespace-pre" data-testid={testid}>{children}</pre>
      <Button variant="ghost" size="sm" className="absolute top-2 right-2 opacity-60 group-hover:opacity-100" onClick={copy} aria-label="Copy"><Copy className="h-3.5 w-3.5" /></Button>
    </div>
  );
};

const SECTIONS = ["Overview", "Authentication", "Create job", "Job status", "Result contract", "Imports", "Webhooks", "Media URLs", "Errors & failure handling", "Webhook tester"];

export default function DocsPage() {
  useSeo({ title: "Lab · API-dokumentasjon", noindex: true });
  const [config, setConfig] = useState(null);
  const [sinkItems, setSinkItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [testUrl, setTestUrl] = useState("");
  const [testJob, setTestJob] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const base = config?.public_base_url || "https://<listing-ai-host>";

  const loadSink = () => api.get("/lab/webhook-sink").then((r) => setSinkItems(r.data.items)).catch(() => {});
  useEffect(() => {
    api.get("/lab/config").then((r) => { setConfig(r.data); setTestUrl(r.data.webhook_sink_url); }).catch((e) => toast.error(errorMessage(e)));
    api.get("/v1/jobs", { params: { limit: 20 } }).then((r) => setJobs(r.data.items)).catch(() => {});
    loadSink();
  }, []);

  const sendTest = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/lab/webhook-test", { url: testUrl || undefined, job_id: testJob || undefined });
      setResult(r.data);
      toast.success(r.data.deliveries?.[0]?.ok ? "Delivered and signature verified" : "Delivery attempted – see result");
      loadSink();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 grid gap-8 lg:grid-cols-[200px_1fr] max-w-[1280px]">
      <nav className="hidden lg:block sticky top-6 self-start space-y-1 text-sm">
        {SECTIONS.map((s) => <a key={s} href={`#${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="block rounded px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-secondary">{s}</a>)}
      </nav>
      <div className="space-y-8 min-w-0">
        <section id="overview">
          <h1 className="text-2xl font-semibold tracking-tight">API & Webhooks</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Listing AI is an AI-processing service. The DigiHome backend is always the gateway: it owns users, properties, listings, approvals and publishing. Listing AI receives images + property data and returns the best documented proposal for listing-ready media and content. Frontends never call this API directly.</p>
          <Code testid="docs-architecture">{`DigiHome web / mobile
        ↓
DigiHome backend  (auth, authorization, rate limits, retries, feature flags, storage, audit)
        ↓  X-API-Key
Listing AI API  ${base}/api/v1
        ↓
AI pipeline  ${config?.default_pipeline_version || "listing-v1.0"}  (intake → analyze → correct [→ enhance] → fidelity review → gallery → listing)
        ↓  signed webhook  (+ GET /jobs/{id} for reconciliation)
DigiHome backend → DigiHome frontend`}</Code>
        </section>

        <section id="authentication" className="space-y-2">
          <h2 className="text-lg font-semibold">Authentication</h2>
          <p className="text-sm text-muted-foreground">Service-to-service key in <code>X-API-Key</code> (or <code>Authorization: Bearer</code>). Keys are rotatable in Settings. Rate limit: {"{RATE_LIMIT_JOBS_PER_MIN}"} job creations per key per minute (HTTP 429). No end-user identity is ever sent to Listing AI.</p>
        </section>

        <section id="create-job" className="space-y-2">
          <h2 className="text-lg font-semibold">Create job – <span className="font-mono text-base">POST /api/v1/jobs</span> → 202</h2>
          <p className="text-sm text-muted-foreground">Asynchronous. Returns a <code>job_id</code> immediately. Use <code>Idempotency-Key</code> so retries never create duplicate jobs (same key + same payload → 200 with the original job; different payload → 409). Provide <code>images[]</code> with short-lived signed URLs, or an <code>import_id</code> from the imports endpoint.</p>
          <p className="text-sm text-muted-foreground"><code>owner_choices</code> are the owner's three framing facts – <code>furnishing</code> (unfurnished | partly_furnished | furnished; omitted → read from the listing text, else asked as the first owner question), <code>styling</code> (digihome | off) and <code>styling_level</code> (furniture_only | furniture_and_textiles). Everything else about the styling (bed size, lamps, rug, curtains) is decided by DigiHome's stylist per room and returned as <code>images[].staging.brief</code>; every render passes both a fidelity and a plausibility review before it is recommended.</p>
          <Code testid="docs-create-job">{`curl -X POST ${base}/api/v1/jobs \\
  -H "X-API-Key: $LISTING_AI_KEY" \\
  -H "Idempotency-Key: digihome-listing-8812-v1" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_property_id": "digihome-property-8812",
    "images": [
      {"external_image_id": "image-1", "url": "https://cdn.digihome.no/signed/..."},
      {"external_image_id": "image-2", "url": "https://cdn.digihome.no/signed/..."}
    ],
    "property_context": {"address": "Thorvald Meyers gate 15", "property_type": "apartment", "area_sqm": 65, "bedrooms": 2},
    "listing_context": {"owner_notes": "Ledig fra 1. oktober"},
    "requested_outputs": {"optimize_images": true, "analyze_rooms": true, "recommend_order": true, "build_listing": true},
    "language": "nb-NO",
    "rental_type": "long_term",
    "owner_choices": {"furnishing": "unfurnished", "styling": "digihome", "styling_level": "furniture_only"},
    "pipeline_version": "${config?.default_pipeline_version || "listing-v1.0"}",
    "options": {"enable_generative_enhancement": false, "strict_fidelity": true},
    "webhook": {"url": "https://api.digihome.no/internal/listing-ai/callback", "secret": "<optional per-job secret>"}
  }'

# 202 Accepted
{"job_id": "job_…", "status": "queued", "stage": "queued", "pipeline_version": "${config?.default_pipeline_version || "listing-v1.0"}", "status_url": "${base}/api/v1/jobs/job_…"}`}</Code>
        </section>

        <section id="job-status" className="space-y-2">
          <h2 className="text-lg font-semibold">Job status – <span className="font-mono text-base">GET /api/v1/jobs/{"{job_id}"}</span></h2>
          <p className="text-sm text-muted-foreground"><code>status</code>: queued → processing → reviewing → completed | failed. <code>stage</code> is a user-friendly stage (analyzing_images, optimizing_images, quality_review, building_listing, completed) with a localized <code>stage_label</code> DigiHome can show as a calm progress state. Never expose internal pipeline steps to homeowners.</p>
          <Code testid="docs-status">{`curl ${base}/api/v1/jobs/job_xxx -H "X-API-Key: $LISTING_AI_KEY"

{"job_id": "job_xxx", "status": "processing", "stage": "optimizing_images", "stage_label": "Forbedrer bildene",
 "progress": {"done": 4, "total": 8, "percent": 50}, "pipeline_version": "listing-v1.0", "images": […partial…]}`}</Code>
        </section>

        <section id="result-contract" className="space-y-2">
          <h2 className="text-lg font-semibold">Result contract (completed)</h2>
          <Code testid="docs-result">{`{
  "job_id": "job_xxx", "status": "completed", "pipeline_version": "listing-v1.0", "language": "nb-NO",
  "images": [{
    "external_image_id": "image-1",
    "original_url": "${base}/api/v1/media/<signed, 1h>",
    "optimized_url": "${base}/api/v1/media/<signed, 1h>",   // null when no candidate passed the fidelity gate
    "thumbnail_url": "…", "output_variant": "processed" | "enhanced" | "original",
    "room_type": "living_room", "room_label": "Stue",
    "quality_score": 0.86, "fidelity_score": 0.97, "fidelity_verdict": "pass",
    "recommended_for_listing": true, "hero_candidate": true,
    "retake_recommended": false, "retake_reason": null, "retake_tip": null,
    "duplicate_of": null, "issues": [], "contains_sensitive_content": false,
    "detected_features": [{"feature": "parquet", "confidence": 0.8, "evidence": "…"}]
  }],
  "gallery": {"recommended_order": ["image-1", "image-4", "image-2"], "recommended_hero_image": "image-1",
              "entries": [{"position": 1, "external_image_id": "image-1", "room_type": "living_room", "label": "Stue - hovedbilde"}],
              "excluded": [{"external_image_id": "image-7", "reason": "duplicate", "duplicate_of": "image-2"}],
              "missing_rooms": [{"room_type": "bathroom", "label": "Bad", "reason": "…"}]},
  "listing": {"suggested_title": "…", "suggested_intro": "…", "suggested_description": "…", "highlights": […], "rooms": […],
              "detected_features": [{"feature": "bedrooms", "value": 2, "confidence": 1.0, "source": "digihome_property_data"},
                                    {"feature": "balcony", "value": true, "confidence": 0.86, "source": "image_analysis"}]},
  "questions": [{"id": "confirm_balcony", "feature": "balcony", "question": "Vi tror boligen har balkong. Stemmer det?", "type": "yes_no", "confidence": 0.86}],
  "missing_shots": [{"room_type": "facade", "label": "Fasade", "reason": "…"}],
  "summary": {"images_total": 8, "images_ready": 6, "images_optimized": 6, "images_retake": 1, "headline": "6 bilder klare", "lines": ["6 klare", "6 optimalisert", "1 anbefales tatt på nytt"]},
  "warnings": [], "error": null,
  "provenance": {"code_version": "…", "vision_provider": "openai", "vision_model": "gpt-5", "enhancer_model": null, "input_hashes": […]},
  "metrics": {"total_ms": 182000, "stage_durations_ms": {…}, "tokens": {…}, "cost_estimate_usd": 0.21, "fidelity_rejects": 0}
}`}</Code>
          <p className="text-sm text-muted-foreground">Rules baked into the contract: originals are immutable and always returned; an image with fidelity verdict other than <code>pass</code> never gets an <code>optimized_url</code>; every image-derived feature with confidence &lt; 0.9 is turned into a question instead of a stated fact; nothing is ever published by Listing AI.</p>
        </section>

        <section id="imports" className="space-y-2">
          <h2 className="text-lg font-semibold">Imports – <span className="font-mono text-base">POST /api/v1/imports</span></h2>
          <p className="text-sm text-muted-foreground">Fetch images + source text from an existing listing through a source adapter (<code>finn</code> verified, <code>airbnb</code> best effort). Requires <code>rights_confirmed: true</code>. Source facts are passed to the pipeline as <em>source information</em>, never as truth. Then create a job with <code>import_id</code>.</p>
          <Code testid="docs-imports">{`curl -X POST ${base}/api/v1/imports -H "X-API-Key: $LISTING_AI_KEY" -H "Content-Type: application/json" \\
  -d '{"source": "finn", "url": "https://www.finn.no/realestate/lettings/ad.html?finnkode=123456789", "rights_confirmed": true}'

# 201
{"import_id": "imp_…", "status": "ready", "images_found": 14,
 "source_listing": {"title": "…", "address": "Bergensveien 29, 0963 Oslo", "property_type": "apartment", "area_sqm": 50, "bedrooms": 2, "key_facts": {…}},
 "images": [{"external_image_id": "finn-123456789-1", "preview_url": "…", "width": 1600, "height": 1067}]}

curl -X POST ${base}/api/v1/jobs -H "X-API-Key: $LISTING_AI_KEY" -H "Content-Type: application/json" \\
  -d '{"external_property_id": "digihome-property-8812", "import_id": "imp_…", "property_context": {}}'`}</Code>
        </section>

        <section id="webhooks" className="space-y-2">
          <h2 className="text-lg font-semibold">Webhooks</h2>
          <p className="text-sm text-muted-foreground">On completion (or failure) Listing AI POSTs the full job result to <code>webhook.url</code>. Retries: 6 attempts with backoff (2 s … 3 min). Verify the HMAC-SHA256 signature, reject timestamps older than 5 minutes, and dedupe on <code>X-ListingAI-Delivery</code>. Keep <code>GET /jobs/{"{id}"}</code> as reconciliation fallback.</p>
          <Code testid="docs-webhook-headers">{`POST <webhook.url>
Content-Type: application/json
X-ListingAI-Event: job.completed | job.failed
X-ListingAI-Delivery: whd_<unique>
X-ListingAI-Timestamp: 1725900000
X-ListingAI-Signature: t=1725900000,v1=<hex hmac_sha256(secret, "{t}.{raw_body}")>

{"event": "job.completed", "delivery_id": "whd_…", "job_id": "job_…", "occurred_at": "…", "data": { …same as GET /jobs/{id}… }}`}</Code>
          <Code testid="docs-webhook-verify">{`# DigiHome backend (Python)
import hmac, hashlib, time

def verify(sig_header: str, raw_body: bytes, secret: str) -> bool:
    parts = dict(p.split("=", 1) for p in sig_header.split(","))
    ts, v1 = int(parts["t"]), parts["v1"]
    if abs(time.time() - ts) > 300:            # replay protection
        return False
    expected = hmac.new(secret.encode(), f"{ts}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)`}</Code>
        </section>

        <section id="media-urls" className="space-y-2">
          <h2 className="text-lg font-semibold">Media URLs</h2>
          <p className="text-sm text-muted-foreground">All image URLs are short-lived (default 1 h) HMAC-signed links served by Listing AI from private object storage (<code>original/</code>, <code>processed/</code>, <code>enhanced/</code>, <code>approved/</code>). There are no permanent public URLs. DigiHome should copy approved outputs into its own storage when the user confirms; expired links return 410.</p>
        </section>

        <section id="errors-failure-handling" className="space-y-2">
          <h2 className="text-lg font-semibold">Errors & failure handling</h2>
          <p className="text-sm text-muted-foreground">Errors use <code>{"{\"detail\": {\"code\", \"message\"}}"}</code>: 401 invalid_api_key, 409 idempotency_conflict, 422 validation/no_images/unknown_pipeline_version, 429 rate_limited. A failed job (<code>status: failed</code>) still lists every image with its <code>original_url</code>, so DigiHome can offer “Prøv igjen” or “Fortsett med originalene”. Per-image failures degrade gracefully: the image is returned as original with <code>error</code> set. Listing AI is never a single point of failure for creating a listing.</p>
        </section>

        <section id="webhook-tester" className="space-y-3">
          <h2 className="text-lg font-semibold">Webhook tester</h2>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Send a signed delivery</CardTitle><CardDescription>Defaults to the built-in sink, which verifies the signature exactly like DigiHome should. Pick a job to send its real payload.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-[1fr_260px]">
                <div className="space-y-1.5"><Label>Target URL</Label><Input value={testUrl} onChange={(e) => setTestUrl(e.target.value)} className="font-mono text-xs" data-testid="webhook-test-url" /></div>
                <div className="space-y-1.5"><Label>Job (optional)</Label>
                  <select value={testJob} onChange={(e) => setTestJob(e.target.value)} className="h-10 w-full rounded-md border bg-card px-3 text-xs font-mono" data-testid="webhook-test-job">
                    <option value="">job.test (synthetic)</option>
                    {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.job_id} · {j.status}</option>)}
                  </select></div>
              </div>
              <Button onClick={sendTest} disabled={sending} data-testid="webhook-test-send">{sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : <><Send className="h-4 w-4 mr-2" />Send signed webhook</>}</Button>
              {result && (
                <div className="rounded-lg border p-3 text-xs space-y-1" data-testid="webhook-test-result">
                  <div>event <span className="font-mono">{result.event}</span> → <span className="font-mono">{result.url}</span></div>
                  {result.deliveries.map((d) => <div key={d.id} className="flex gap-2"><span className={cn("h-2 w-2 rounded-full mt-1.5", d.ok ? "bg-[hsl(var(--pass))]" : "bg-[hsl(var(--reject))]")} /><span>attempt {d.attempt}: HTTP {d.status_code ?? "–"} {d.error || ""} · {d.latency_ms} ms {d.response_excerpt ? `· ${d.response_excerpt}` : ""}</span></div>)}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between"><div><CardTitle className="text-base">Sink: received deliveries</CardTitle><CardDescription className="font-mono text-xs">{config?.webhook_sink_url}</CardDescription></div><Button variant="ghost" size="sm" onClick={loadSink} data-testid="sink-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button></CardHeader>
            <CardContent>
              <div className="space-y-1 text-xs font-mono max-h-72 overflow-auto scrollbar-thin" data-testid="webhook-sink-list">
                {sinkItems.length === 0 && <div className="text-muted-foreground">Nothing received yet.</div>}
                {sinkItems.map((s, i) => <div key={i} className="flex flex-wrap gap-2 py-1 border-b last:border-0"><span className={cn("rounded px-1.5", s.signature_valid ? "verdict-pass border" : "verdict-reject border")}>{s.signature_valid ? "signature ok" : s.reason}</span><span>{s.event}</span><span className="text-muted-foreground">{s.job_id}</span>{s.duplicate && <span className="verdict-needs_review border rounded px-1.5">duplicate</span>}<span className="ml-auto text-muted-foreground">{fmtDate(s.received_at)}</span></div>)}
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
